import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../atoms/Icon';
import SessionManager from '../../utils/sessionManager';

const HrDashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Get HR session data
  const getHrSessionData = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return {
      name: hrSession?.profile?.full_name || hrSession?.profile?.contact_name || hrSession?.contact_name || 'HR User',
      email: hrSession?.profile?.email || hrSession?.email || '',
      company: hrSession?.company || hrSession?.profile?.company_name || 'Company'
    };
  };

  const { name: hrName, email: hrEmail, company: companyName } = getHrSessionData();

  const expanded = hovered;

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', path: '/hr/dashboard' },
    { id: 'jobs', label: 'Jobs', icon: 'work', path: '/hr/jobs' },
    { id: 'applications', label: 'Applications', icon: 'description', path: '/hr/applications' },
    { id: 'document-verification', label: 'Document Verification', icon: 'verified', path: '/hr/document-verification' },
    { id: 'candidates', label: 'Candidates', icon: 'people', path: '/hr/candidates' },
    { id: 'interviews', label: 'Interviews', icon: 'event', path: '/hr/interviews' },
    { id: 'analytics', label: 'Analytics', icon: 'analytics', path: '/hr/analytics' },
    { id: 'messages', label: 'Messages', icon: 'mail', path: '/hr/messages' },
    { id: 'settings', label: 'Settings', icon: 'settings', path: '/hr/settings' },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    SessionManager.clearSession('hr');
    navigate('/hr_login');
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    if (!isLoggedInAsHr) {
      navigate('/hr_login');
    }
  }, [navigate]);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ===== SIDEBAR ===== */}
      {/* Desktop: inline sidebar with hover expand */}
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`
          hidden md:flex flex-col flex-shrink-0 bg-white border-r border-gray-200
          transition-all duration-300 ease-in-out overflow-hidden
          ${expanded ? 'w-64' : 'w-[70px]'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors overflow-hidden"
            title="Go to Home"
          >
            <span className="text-xl font-bold flex-shrink-0">A</span>
            <span className={`text-xl font-bold whitespace-nowrap transition-all duration-300 ${expanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
              ptirix HR
            </span>
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
          <ul className="space-y-1 px-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.path)}
                  title={!expanded ? item.label : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap overflow-hidden ${
                    isActive(item.path)
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={item.icon} size={20} className="flex-shrink-0" />
                  <span className={`text-sm font-medium transition-all duration-300 ${expanded ? 'opacity-100' : 'opacity-0 w-0'}`}>
                    {item.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="border-t border-gray-200 p-3 flex-shrink-0 overflow-hidden">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white font-semibold flex-shrink-0">
              {hrName.charAt(0).toUpperCase()}
            </div>
            <div className={`min-w-0 transition-all duration-300 ${expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
              <p className="text-sm font-semibold text-gray-900 truncate">{hrName}</p>
              <p className="text-xs text-gray-500 truncate">{companyName}</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            title={!expanded ? 'Logout' : undefined}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap overflow-hidden"
          >
            <Icon name="logout" size={18} className="flex-shrink-0" />
            <span className={`text-sm font-medium transition-all duration-300 ${expanded ? 'opacity-100' : 'opacity-0 w-0'}`}>
              Logout
            </span>
          </button>
        </div>
      </aside>

      {/* Mobile: slide-over sidebar */}
      <aside
        className={`
          md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col
          transition-transform duration-300 ease-in-out shadow-xl
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0">
          <button onClick={() => navigate('/')} className="text-xl font-bold text-teal-600 hover:text-teal-700 transition-colors">
            Aptirix HR
          </button>
          <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={item.icon} size={20} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User */}
        <div className="border-t border-gray-200 p-4 flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white font-semibold">
              {hrName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{hrName}</p>
              <p className="text-xs text-gray-500 truncate">{companyName}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <Icon name="logout" size={18} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* ===== MAIN CONTENT ===== */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Header Bar */}
        <header className="h-16 bg-gradient-to-r from-teal-700 to-teal-800 flex items-center justify-between px-4 md:px-6 shadow-md flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white md:hidden"
            >
              <Icon name="menu" size={20} />
            </button>
            <input
              type="text"
              placeholder="Search candidates, jobs, applications..."
              className="w-40 sm:w-60 md:w-80 px-4 py-2 rounded-lg bg-white/90 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-white text-xs md:text-sm hidden sm:block">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
            <button className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white relative">
              <Icon name="notifications" size={20} />
            </button>
            <button className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white relative">
              <Icon name="mail" size={20} />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default HrDashboardLayout;
